import { holdPath, holdStrokeWidth } from '../hold-outline';
import { isDrawableLine, strokeRouteLine } from '../route-line';
import type { Hold, RouteLine } from '../../../types/climb';

/**
 * **The climb's card**: its photo with its holds ringed, the grade and the
 * name of its holds written across the top of the photo, and under it a band
 * with the average of its stars. One
 * drawing for every place it goes — the dialog's preview, the picture shared
 * on its own, and (from the editor) every frame of a video it is laid over —
 * so what is previewed is what is exported.
 *
 * Everything is measured in shares of the card's width, so the same code draws
 * a 200 px preview and a 1080 px export.
 */

/** The picture a card is drawn from, the photo effect already painted on it. */
export interface ThumbnailPicture {
  image: CanvasImageSource;
  /** The image's own size in pixels: the holds are fractions of it. */
  width: number;
  height: number;
  /** The holds to ring, as fractions of the picture (the reader's holds mode already applied). */
  holds: Hold[];
  /**
   * The holds that light up when the card is made to shine: all of the climb's,
   * whichever of them the reader's holds mode rings — it is the climb that shines.
   */
  glowHolds: Hold[];
  /** A route drawn as a line, when the reader has the line on. */
  line: RouteLine | null;
}

/** The words on the card. A line with nothing to say is left out. */
export interface ThumbnailInfo {
  /** Null where the gym hides it (`grade_visible`). */
  grade: string | null;
  /** The hold type's name, e.g. "Astronavi celesti" (`climb.hold_color`). */
  holdType: string | null;
  /** Its swatches (`climb.hold_color_hex`). */
  holdColors: string[];
  /** The average of the stars, null with none given. */
  rating: number | null;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * How far from its own shape a photo may be cut to: a phone's 9:16 shot would
 * otherwise make a card taller than the video it sits on, and a panorama one
 * too thin to read. Cut around the holds, so the climb stays in it.
 */
const PHOTO_ASPECT_MIN = 3 / 4;
const PHOTO_ASPECT_MAX = 4 / 3;

// The words, in shares of the card's width.
const PAD_X = 0.06;
const PAD_Y = 0.045;
const GRADE_ROW = 0.14;
const GRADE_FONT = 0.12;
const HOLDS_ROW = 0.09;
const HOLDS_FONT = 0.058;
const SWATCH = 0.046;
const STARS_ROW = 0.09;
const STAR = 0.055;
/** How far down the photo the shade behind the words reaches, past the last line. */
const CAPTION_SHADE = 0.12;

/** How far the light spills past a hold, as a share of the card's width. */
const GLOW_SPREAD = 0.06;

const CORNER = 0.05;
const BORDER = 0.012;
/** The thinnest a ring may be, in canvas pixels, so a hold is still circled on a small card. */
const MIN_RING_PX = 2.5;

const BAND_COLOR = 'rgba(17,17,17,0.92)';
const STAR_COLOR = '#FACC15';
const FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

export interface ThumbnailLayout {
  width: number;
  height: number;
  /** The part of the picture shown, in its own pixels. */
  crop: Rect;
  photoHeight: number;
  /** The grade, over the top of the photo. */
  showsGrade: boolean;
  /** The name of the holds, over the top of the photo under the grade. */
  showsHoldType: boolean;
  /** The stars, in the band under the photo — the band is there only for them. */
  showsRating: boolean;
}

function bandHeight(info: ThumbnailInfo, width: number): number {
  return info.rating != null ? (STARS_ROW + PAD_Y * 2) * width : 0;
}

/** The middle of the holds, where a cut photo is centred: the photo's own middle without any. */
function holdsCentre(picture: ThumbnailPicture): { x: number; y: number } {
  if (picture.holds.length === 0) return { x: 0.5, y: 0.5 };
  const left = Math.min(...picture.holds.map((hold) => hold.x - hold.width / 2));
  const right = Math.max(...picture.holds.map((hold) => hold.x + hold.width / 2));
  const top = Math.min(...picture.holds.map((hold) => hold.y - hold.height / 2));
  const bottom = Math.max(...picture.holds.map((hold) => hold.y + hold.height / 2));
  return { x: (left + right) / 2, y: (top + bottom) / 2 };
}

/** The part of the picture the card shows: all of it, unless its shape is too far out. */
export function thumbnailCrop(picture: ThumbnailPicture): Rect {
  const { width: W, height: H } = picture;
  const aspect = W / H;
  const target = Math.min(PHOTO_ASPECT_MAX, Math.max(PHOTO_ASPECT_MIN, aspect));
  const centre = holdsCentre(picture);

  if (aspect > target) {
    const width = H * target;
    const x = Math.min(W - width, Math.max(0, centre.x * W - width / 2));
    return { x, y: 0, width, height: H };
  }
  if (aspect < target) {
    const height = W / target;
    const y = Math.min(H - height, Math.max(0, centre.y * H - height / 2));
    return { x: 0, y, width: W, height };
  }
  return { x: 0, y: 0, width: W, height: H };
}

export function thumbnailLayout(picture: ThumbnailPicture, info: ThumbnailInfo, width: number): ThumbnailLayout {
  const crop = thumbnailCrop(picture);
  const photoHeight = width * (crop.height / crop.width);
  return {
    width,
    height: photoHeight + bandHeight(info, width),
    crop,
    photoHeight,
    showsGrade: !!info.grade,
    showsHoldType: !!info.holdType,
    showsRating: info.rating != null,
  };
}

export function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arc(x + w - r, y + r, r, -Math.PI / 2, 0);
  ctx.lineTo(x + w, y + h - r);
  ctx.arc(x + w - r, y + h - r, r, 0, Math.PI / 2);
  ctx.lineTo(x + r, y + h);
  ctx.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
  ctx.lineTo(x, y + r);
  ctx.arc(x + r, y + r, r, Math.PI, Math.PI * 3 / 2);
  ctx.closePath();
}

/** Truncate `text` with an ellipsis so it fits within `maxWidth`. Set ctx.font first. */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

function starPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? r : r * 0.45;
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const px = cx + radius * Math.cos(angle);
    const py = cy + radius * Math.sin(angle);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

/** Five stars filled as far as the average goes, and the average itself. Returns the width drawn. */
function drawStars(ctx: CanvasRenderingContext2D, x: number, cy: number, rating: number, width: number): number {
  const r = (STAR * width) / 2;
  const step = r * 2.15;
  for (let i = 0; i < 5; i++) {
    const cx = x + r + i * step;
    starPath(ctx, cx, cy, r);
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.fill();
    const filled = Math.min(1, Math.max(0, rating - i));
    if (filled > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(cx - r, cy - r, r * 2 * filled, r * 2);
      ctx.clip();
      starPath(ctx, cx, cy, r);
      ctx.fillStyle = STAR_COLOR;
      ctx.fill();
      ctx.restore();
    }
  }
  const starsWidth = r * 2 + step * 4;
  ctx.font = `600 ${HOLDS_FONT * width}px ${FONT}`;
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  const value = rating.toFixed(1);
  ctx.fillText(value, x + starsWidth + r * 0.8, cy);
  return starsWidth + r * 0.8 + ctx.measureText(value).width;
}

/** The photo, cut to the layout's crop, with the climb's holds ringed (and its line). */
function drawPhoto(ctx: CanvasRenderingContext2D, x: number, y: number, layout: ThumbnailLayout, picture: ThumbnailPicture) {
  const { crop, width, photoHeight } = layout;
  const scale = width / crop.width;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, photoHeight);
  ctx.clip();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(picture.image, crop.x, crop.y, crop.width, crop.height, x, y, width, photoHeight);

  // The picture's own pixels from here, so a hold's path is drawn as it is stored.
  const left = x - crop.x * scale;
  const top = y - crop.y * scale;
  if (picture.holds.length > 0 && typeof Path2D !== 'undefined') {
    ctx.save();
    ctx.translate(left, top);
    ctx.scale(scale, scale);
    const referenceEdge = Math.max(crop.width, crop.height);
    for (const hold of picture.holds) {
      ctx.lineWidth = Math.max(holdStrokeWidth(hold, picture.width, picture.height, referenceEdge), MIN_RING_PX / scale);
      ctx.strokeStyle = hold.color || 'red';
      ctx.stroke(new Path2D(holdPath(hold, picture.width, picture.height)));
    }
    ctx.restore();
  }
  if (isDrawableLine(picture.line)) {
    strokeRouteLine(ctx, picture.line, left, top, picture.width * scale, picture.height * scale);
  }
  ctx.restore();
}

/**
 * The grade and the name of the holds, across the top of the photo, over a
 * shade that fades into it so they read on a white wall as on a dark one.
 */
function drawCaption(ctx: CanvasRenderingContext2D, x: number, y: number, layout: ThumbnailLayout, info: ThumbnailInfo) {
  const { width, photoHeight } = layout;
  if (!layout.showsGrade && !layout.showsHoldType) return;

  const linesHeight = (layout.showsGrade ? GRADE_ROW : 0) + (layout.showsHoldType ? HOLDS_ROW : 0);
  const shadeHeight = Math.min(photoHeight, (PAD_Y + linesHeight + CAPTION_SHADE) * width);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, photoHeight);
  ctx.clip();
  const shade = ctx.createLinearGradient(0, y, 0, y + shadeHeight);
  shade.addColorStop(0, 'rgba(0,0,0,0.72)');
  shade.addColorStop(0.6, 'rgba(0,0,0,0.4)');
  shade.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shade;
  ctx.fillRect(x, y, width, shadeHeight);

  const left = x + PAD_X * width;
  const right = x + width - PAD_X * width;
  let rowTop = y + PAD_Y * width;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = width * 0.02;

  if (layout.showsGrade && info.grade) {
    const cy = rowTop + (GRADE_ROW * width) / 2;
    ctx.font = `800 ${GRADE_FONT * width}px ${FONT}`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(fitText(ctx, info.grade, right - left), left, cy);
    rowTop += GRADE_ROW * width;
  }

  if (layout.showsHoldType && info.holdType) {
    const cy = rowTop + (HOLDS_ROW * width) / 2;
    const r = (SWATCH * width) / 2;
    let cursor = left;
    for (const colour of info.holdColors.slice(0, 3)) {
      ctx.beginPath();
      ctx.arc(cursor + r, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = colour;
      ctx.fill();
      ctx.lineWidth = Math.max(1, width * 0.004);
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.stroke();
      cursor += r * 2 + width * 0.012;
    }
    if (info.holdColors.length > 0) cursor += width * 0.015;
    ctx.font = `700 ${HOLDS_FONT * width}px ${FONT}`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(fitText(ctx, info.holdType, right - cursor), cursor, cy);
  }
  ctx.restore();
}

/** The band under the photo, with the stars. */
function drawBand(ctx: CanvasRenderingContext2D, x: number, y: number, layout: ThumbnailLayout, info: ThumbnailInfo) {
  const { width, photoHeight, height } = layout;
  if (!layout.showsRating || info.rating == null) return;
  const top = y + photoHeight;
  ctx.fillStyle = BAND_COLOR;
  ctx.fillRect(x, top, width, height - photoHeight);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  drawStars(ctx, x + PAD_X * width, top + (height - photoHeight) / 2, info.rating, width);
}

/**
 * The light the climb's holds give off, at full strength, drawn where the card
 * would be: each hold brightened and haloed in its own colour (a route's line
 * too). Meant for a layer of its own, laid over the card at the strength of
 * the moment (see `celebrationGlow`) — drawn once, and only faded frame by
 * frame.
 *
 * Painted over rather than added (`lighter`): added light cannot brighten a
 * white wall, and most gym walls are white, so there the halo is the hold's
 * colour spilling onto the wall, and the brightening is for the dark ones.
 */
export function drawThumbnailGlow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  layout: ThumbnailLayout,
  picture: ThumbnailPicture,
  { framed = false }: DrawThumbnailOptions = {},
) {
  const { crop, width, photoHeight } = layout;
  const scale = width / crop.width;
  const left = x - crop.x * scale;
  const top = y - crop.y * scale;
  const spread = GLOW_SPREAD * width;

  ctx.save();
  if (framed) {
    roundRectPath(ctx, x, y, width, layout.height, CORNER * width);
    ctx.clip();
  }
  ctx.beginPath();
  ctx.rect(x, y, width, photoHeight);
  ctx.clip();

  if (picture.glowHolds.length > 0 && typeof Path2D !== 'undefined') {
    ctx.save();
    ctx.translate(left, top);
    ctx.scale(scale, scale);
    const referenceEdge = Math.max(crop.width, crop.height);
    for (const hold of picture.glowHolds) {
      const path = new Path2D(holdPath(hold, picture.width, picture.height));
      const colour = hold.color || 'red';
      // The blur is in the canvas's own pixels whatever the transform, so it
      // spreads the same on a big hold and a small one.
      ctx.shadowColor = colour;
      ctx.shadowBlur = spread;
      ctx.lineWidth = Math.max(holdStrokeWidth(hold, picture.width, picture.height, referenceEdge) * 2.2, (MIN_RING_PX * 2) / scale);
      ctx.strokeStyle = colour;
      // Twice, the halo being the shadow of the line: once is a faint one.
      ctx.stroke(path);
      ctx.stroke(path);
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 0.28;
      ctx.fillStyle = colour;
      ctx.fill(path);
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.fill(path);
    }
    ctx.restore();
  }
  if (isDrawableLine(picture.line)) {
    ctx.shadowColor = '#ffffff';
    ctx.shadowBlur = spread;
    strokeRouteLine(ctx, picture.line, left, top, picture.width * scale, picture.height * scale);
  }
  ctx.restore();
}

export interface DrawThumbnailOptions {
  /** A card laid over something (a video) has round corners and a white edge; a picture of its own does not. */
  framed?: boolean;
}

/** Draw the card with its top-left corner at (x, y), at the layout's size. */
export function drawThumbnail(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  layout: ThumbnailLayout,
  picture: ThumbnailPicture,
  info: ThumbnailInfo,
  { framed = false }: DrawThumbnailOptions = {},
) {
  const { width, height } = layout;
  const radius = CORNER * width;
  ctx.save();
  if (framed) {
    roundRectPath(ctx, x, y, width, height, radius);
    ctx.clip();
  }
  drawPhoto(ctx, x, y, layout, picture);
  drawCaption(ctx, x, y, layout, info);
  drawBand(ctx, x, y, layout, info);
  ctx.restore();

  if (framed) {
    ctx.save();
    roundRectPath(ctx, x, y, width, height, radius);
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = Math.max(2, BORDER * width);
    ctx.stroke();
    ctx.restore();
  }
}
